import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShoppingCart, CreditCard, ChevronLeft, Check, Download, Package, Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fmt, priceOf, isFree } from "@/pages/shop/Shop";
import Seo from "@/components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const { lang, pickL } = useI18n();
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const de = lang === "de";
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get(`/shop/products/${slug}`).then((r) => { setP(r.data); setImg(0); }).catch(() => setNotFound(true));
  }, [slug]);

  const claimFree = async () => {
    if (!user) { nav(`/login?next=${encodeURIComponent("/shop/" + slug)}`); return; }
    if (!window.confirm(de ? `„${pickL(p, "name")}“ kostenlos zu Ihrem Konto hinzufügen?` : `Add "${pickL(p, "name")}" to your account for free?`)) return;
    setClaiming(true);
    try {
      await api.post("/shop/claim-free", { product_id: p.id });
      toast.success(de ? "Kostenlos freigeschaltet! Sie finden es unter „Meine Käufe“." : "Unlocked for free! Find it under My Purchases.");
      nav("/portal/purchases");
    } catch (e) { toast.error(apiErr(e)); } finally { setClaiming(false); }
  };

  if (notFound) return (
    <div className="container-x py-32 text-center">
      <p className="text-muted-foreground">{de ? "Produkt nicht gefunden." : "Product not found."}</p>
      <Link to="/shop" className="btn-primary mt-6 inline-flex">{de ? "Zum Shop" : "Back to shop"}</Link>
    </div>
  );
  if (!p) return <div className="container-x py-32 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  const onSale = p.sale_price && p.sale_price < p.price;
  const TypeIcon = { physical: Package, digital: Download, software: Cpu }[p.type] || Download;

  return (
    <div className="container-x py-10">
      <Seo title={pickL(p, "name")} description={pickL(p, "short")} image={(p.images || [])[0]} />
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm link-muted mb-6" data-testid="pd-back"><ChevronLeft size={16} /> {de ? "Zurück zum Shop" : "Back to shop"}</Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="surface aspect-[4/3] overflow-hidden flex items-center justify-center bg-secondary">
            {(p.images || [])[img]
              ? <img src={p.images[img]} alt={pickL(p, "name")} className="h-full w-full object-cover" data-testid="pd-image" />
              : <TypeIcon size={64} className="text-muted-foreground" />}
          </div>
          {(p.images || []).length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setImg(i)} className={`h-16 w-16 rounded-md overflow-hidden border-2 ${i === img ? "border-primary" : "border-transparent"}`}>
                  <img src={im} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold"><TypeIcon size={13} /> {p.type}</span>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-4">{pickL(p, "name")}</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">{pickL(p, "short")}</p>

          <div className="mt-6 flex items-baseline gap-3">
            {isFree(p) ? (
              <span className="font-display text-4xl font-black text-green-500" data-testid="pd-price-free">{de ? "Kostenlos" : "Free"}</span>
            ) : (
              <>
                {onSale && <span className="text-lg text-muted-foreground line-through">{fmt(p.price)}</span>}
                <span className="font-display text-4xl font-black">{fmt(priceOf(p))}</span>
              </>
            )}
          </div>
          {!isFree(p) && <p className="text-xs text-muted-foreground mt-1">{de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."}</p>}

          <div className="mt-8 space-y-3">
            {isFree(p) ? (
              <button onClick={claimFree} disabled={claiming} className="btn-primary w-full !py-3" data-testid="pd-claim-free">
                {claiming ? <Loader2 size={17} className="animate-spin" /> : <><Download size={17} /> {de ? (["digital", "software"].includes(p.type) ? "Gratis herunterladen" : "Kostenlos erhalten") : "Get for free"}</>}
              </button>
            ) : (
              <>
                <button onClick={() => { add(p); toast.success(de ? "Zum Warenkorb hinzugefügt" : "Added to cart"); }} className="btn-primary w-full !py-3" data-testid="pd-add-cart">
                  <ShoppingCart size={17} /> {de ? "In den Warenkorb" : "Add to cart"}
                </button>
                <button onClick={() => { add(p); nav("/shop/cart"); }} className="btn-ghost w-full !py-3" data-testid="pd-buy-now">
                  <CreditCard size={17} /> {de ? "Sofort kaufen" : "Buy now"}
                </button>
              </>
            )}
          </div>

          {p.type === "physical" && p.stock != null && (
            <p className={`text-sm mt-4 ${p.stock > 0 ? "text-green-500" : "text-red-500"}`}>{p.stock > 0 ? (de ? `${p.stock} auf Lager` : `${p.stock} in stock`) : (de ? "Ausverkauft" : "Out of stock")}</p>
          )}
        </div>
      </div>

      {(pickL(p, "desc") || p.system_requirements || p.version) && (
        <div className="grid lg:grid-cols-3 gap-8 mt-16">
          <div className="lg:col-span-2 prose-invert max-w-none">
            <h2 className="font-display text-2xl font-black mb-4">{de ? "Beschreibung" : "Description"}</h2>
            <div className="text-muted-foreground leading-relaxed whitespace-pre-line">{pickL(p, "desc")}</div>
          </div>
          <div className="space-y-4">
            {p.version && <div className="surface p-5"><div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{de ? "Version" : "Version"}</div><div className="font-semibold">{p.version}</div>{p.changelog && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{p.changelog}</p>}</div>}
            {p.system_requirements && <div className="surface p-5"><div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{de ? "Systemanforderungen" : "System requirements"}</div><p className="text-sm text-muted-foreground whitespace-pre-line">{p.system_requirements}</p></div>}
            {["digital", "software"].includes(p.type) && <div className="surface p-5 flex items-start gap-3"><Check size={18} className="text-primary shrink-0 mt-0.5" /><p className="text-sm">{de ? "Sofortiger Download nach dem Kauf in Ihrem Kundenportal." : "Instant download after purchase in your customer portal."}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}
